/* eslint-disable arrow-body-style */
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { useFormik, FormikProvider } from "formik";
import * as Yup from "yup";
import _ from "lodash";
import { HStack, Spinner, VStack } from "../../../components/utils";
import BCHeader from "../../../shared/BCHeader";
import ExerciseDetailsStepper from "./ExerciseDetailsStepper";
import { ExerciseDetailsNavigation } from "./ExerciseDetailsNavigation";
import {
  defaultTransactionDetailsData,
  TransactionDetailsData,
} from "../../../types/Transaction";
import {
  getTransactionDetails,
  saveTransactionDetails,
} from "../../../api/TransactionApisV2";
import { MonetaryEventState } from "../../../types/OptionBuyback";

const AddExerciseDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { id, mode } = params;

  const [currentStep, setCurrentStep] = useState(1);

  function onStepChange(step?: number) {
    setCurrentStep(step || parseInt(`${currentStep}`, 10) + 1);
  }

  function onBackClick(step?: number) {
    if (currentStep !== 1) {
      setCurrentStep(step || parseInt(`${currentStep}`, 10) - 1);
    }
  }

  // Validation schemas for each step
  const step1ValidationSchema = Yup.object({
    eventName: Yup.string().required("Exercise Name is required"),
    monetaryDetails: Yup.object().shape({
      participantListMethod: Yup.string().required(
        "Participant List is required"
      ),
    }),
    startDate: Yup.date().required("Start Date is required"),
    endDate: Yup.date().when("recurringSetting.isRecurring", {
      is: false,
      then: Yup.date()
        .required("End Date is required when Recurring Exercise is disabled")
        .min(Yup.ref("startDate"), "End Date cannot be before Start Date"),
      otherwise: Yup.date().notRequired(),
    }),
    vestingCutOffDate: Yup.date().when("eventState", {
      is: (state: string) =>
        state !== MonetaryEventState.STARTED &&
        state !== MonetaryEventState.CLOSED &&
        state !== MonetaryEventState.COMPLETED,
      then: Yup.date()
        .required("Vesting Cutoff Date is required")
        .test(
          "not-in-past",
          "Vesting Cutoff Date cannot be in the past",
          (value) => {
            if (!value) return false;
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const input = new Date(value);
            input.setHours(0, 0, 0, 0);
            return input >= today;
          }
        ),
      otherwise: Yup.date().notRequired(),
    }),
    recurringSetting: Yup.object().shape({
      isRecurring: Yup.boolean().required(),
      recurringInterval: Yup.number().when("isRecurring", {
        is: true,
        then: Yup.number()
          .required("Recurring Interval is required when enabled")
          .min(1, "Recurring Interval must be at least 1"),
        otherwise: Yup.number().notRequired(),
      }),
      noOfRecurrences: Yup.number().when("isRecurring", {
        is: true,
        then: Yup.number()
          .required("Number of Recurrences is required when enabled")
          .min(1, "Number of Recurrences must be at least 1"),
        otherwise: Yup.number().notRequired(),
      }),
    }),
    askWillingness: Yup.boolean(),
    minWillingnessPercentage: Yup.number().when("askWillingness", {
      is: true,
      then: Yup.number().max(100, "Minimum Willingness cannot exceed 100%"),
      otherwise: Yup.number().notRequired(),
    }),
  });

  const step2ValidationSchema = Yup.object({});

  const validationSchemas = [step1ValidationSchema, step2ValidationSchema];

  const [loading, setLoading] = useState(true);
  const [initialValues, setInitialValues] = useState<TransactionDetailsData>(
    defaultTransactionDetailsData
  );

  useEffect(() => {
    const fetchInitialValues = async () => {
      setLoading(true);
      try {
        const response = await getTransactionDetails(id);
        setInitialValues(response.data as TransactionDetailsData);
      } catch (error) {
        setInitialValues(defaultTransactionDetailsData);
      } finally {
        setLoading(false);
      }
    };
    fetchInitialValues();
  }, [id]);

  const formik = useFormik({
    initialValues,
    validationSchema: validationSchemas[currentStep - 1],
    onSubmit: () => {},
    enableReinitialize: true,
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center w-full h-full">
        <Spinner />
      </div>
    );
  }

  return (
    <FormikProvider value={formik}>
      <VStack className="h-full gap-4">
        <div className={`flex items-baseline justify-between`}>
          <BCHeader
            bcTitle={"Transactions"}
            className="py-1"
            bcSubTitle={"Exercise"}
          />
        </div>
        <HStack className="flex h-full min-h-full gap-4 ">
          <VStack className="flex w-[25%] bg-white rounded-md min-w-72 ">
            <ExerciseDetailsStepper
              currentStep={currentStep}
              setCurrentStep={setCurrentStep}
            />
          </VStack>
          <VStack className="flex w-[75%] bg-white rounded-md min-w-96">
            <ExerciseDetailsNavigation
              currentStep={currentStep}
              onStepChange={onStepChange}
              onBackClick={onBackClick}
              validationSchemas={validationSchemas}
            />
          </VStack>
        </HStack>
      </VStack>
    </FormikProvider>
  );
};

export default AddExerciseDetails;
